import type React from 'react';
import {useEffect, useRef, useState} from 'react';
import type {
  EntityIdentifierEnum,
  FormDefinitionResponse,
  FormFieldValueRequest,
  FormRequest,
  FormResponse,
} from 'src/api/ProactApiClient';
import {FormFieldTypeEnum} from 'src/api/ProactApiClient';
import type {ITransitioningResponse} from 'src/app/forms/FormTransitionBar';
import {deleteUnsavedFormUploads} from 'src/app/forms/offline/localFiles';
import type {OfflineFormCreateRequest} from 'src/app/forms/offline/queuedTransition';
import {isEffectivelyOffline} from 'src/offline/connectivity';
import {isOfflineQueuedResult} from 'src/offline/offlineMutation';
import type {ProactFormApi} from 'src/app/forms/engine/useProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  useCreateFormMutation,
  useLazyGetFormQuery,
  useUpdateFormMutation,
} from 'src/redux/queries/form';
import {
  useLazyGetFormDefinitionFlattenedQuery,
  useLazyGetFormDefinitionFlattenedVersionQuery,
} from 'src/redux/queries/formDefinition';
import type {IFormData} from 'src/types/Form';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import type {
  IStateMachine,
  IStateMachineTransition,
} from 'src/types/StateMachine';
import {tryParse} from 'src/utilities/objectUtils';
import {identifierFieldName} from 'src/types/FormV2';
import {mapFormDefinitionResponseToFormFieldsV2} from '../formHelpers';
import {
  buildQualifiedFieldName,
  normalizeFormDefinitionSubFormFieldIds,
  normalizeFormFieldValues,
  resolveQualifiedFieldValueName,
  splitQualifiedFieldName,
} from 'src/types/formFieldQualifiedName';

export interface UseFormSubmissionEditorOptions {
  formDefinitionId: string | number;
  formSubmissionId?: string | number;
  taskAssignmentId?: string;
  extensionTabId?: string | number;
  targetEntityType?: string | number;
  targetEntityId?: string | number;
}

/** A save outcome, plus whether it was queued to the offline outbox. */
export type FormSaveResult = ITransitioningResponse & {offlineQueued?: boolean};

export interface UseFormSubmissionEditorResult {
  form: FormResponse | undefined;
  formDefinition: FormDefinitionResponse | undefined;
  formName: string | undefined;
  formApi: ProactFormApi;
  formMeta: IFormFieldV2[];
  formReferenceImage: string | undefined;
  isLoading: boolean;
  isSavePending: boolean;
  currentFormId: React.MutableRefObject<number | null>;
  stateMachine: IStateMachine | undefined;
  handleValidateAndSave: (
    transition?: IStateMachineTransition
  ) => Promise<FormSaveResult>;
}

/** Builds the initial submission values for a new (unsaved) form. */
const buildDefaultFormData = (formMeta: IFormFieldV2[]): IFormData => {
  const data: IFormData = {};
  formMeta
    .filter(field => field.name !== identifierFieldName)
    .forEach(field => {
      data[field.name] = field.builderOptions?.defaultValue ?? '';
      if (field.type === 'switch') {
        data[field.name] = field.builderOptions?.defaultValue === 'true';
      }
    });
  return data;
};

export const useFormSubmissionEditor = ({
  formDefinitionId,
  formSubmissionId,
  taskAssignmentId,
  extensionTabId,
  targetEntityType,
  targetEntityId,
}: UseFormSubmissionEditorOptions): UseFormSubmissionEditorResult => {
  const {setAlert} = useAlert();

  const defId = String(formDefinitionId);
  const subId = formSubmissionId ? String(formSubmissionId) : undefined;

  const [form, setForm] = useState<FormResponse | undefined>();
  const formVersion = form?.formDefinition?.version;
  const [formName, setFormName] = useState<string | undefined>();
  const [formDefinition, setFormDefinition] = useState<
    FormDefinitionResponse | undefined
  >();

  const [formMeta, setFormMeta] = useState<IFormFieldV2[]>([]);

  const [formReferenceImage, setFormReferenceImage] = useState<
    string | undefined
  >();
  const [isSavePending, setIsSavePending] = useState(false);

  const [isFormLoading, setIsFormLoading] = useState(!!subId);
  const [isDefinitionLoading, setIsDefinitionLoading] = useState(true);
  const isLoading = isFormLoading || isDefinitionLoading;

  const currentFormId = useRef<number | null>(null);
  // Tracks whether a create's captured uploads were queued; if not, abandoning
  // the page frees their orphaned blobs on unmount.
  const uploadsQueuedRef = useRef(false);
  const loadedValueIdsRef = useRef<Record<string, number>>({});
  // select-multiple: field name -> option id -> row id, so an unchanged option
  // keeps its row on save.
  const loadedOptionRowIdsRef = useRef<Record<string, Record<string, number>>>(
    {}
  );

  const [triggerGetForm] = useLazyGetFormQuery();
  const [triggerGetFormDefinition] = useLazyGetFormDefinitionFlattenedQuery();
  const [triggerGetFormDefinitionVersion] =
    useLazyGetFormDefinitionFlattenedVersionQuery();
  const [createForm] = useCreateFormMutation();
  const [updateForm] = useUpdateFormMutation();

  const formApi = useProactForm({formMeta});
  const {resetForm} = formApi;

  // Read through a ref so the unmount cleanup keeps empty deps.
  const getValuesRef = useRef(formApi.methods.getValues);
  getValuesRef.current = formApi.methods.getValues;

  useEffect(() => {
    if (subId !== undefined) return;
    return () => {
      if (!uploadsQueuedRef.current) {
        void deleteUnsavedFormUploads(getValuesRef.current());
      }
    };
  }, []);

  // Extract reference image from form definition metadata
  useEffect(() => {
    if (formDefinition === undefined) return;
    try {
      const img = tryParse(
        tryParse(formDefinition.metadata?.cache)?.referenceImage
      )[0].value;
      setFormReferenceImage(img);
    } catch {
      /* no reference image */
    }
  }, [formDefinition]);

  // Initialize default form data for new submissions
  useEffect(() => {
    if (formMeta.length === 0) return;
    if (subId !== undefined) return;

    resetForm(buildDefaultFormData(formMeta));
  }, [formMeta, subId, resetForm]);

  // Load existing form submission (values applied once the metadata is ready)
  useEffect(() => {
    const getData = async () => {
      try {
        if (subId === undefined) {
          setIsFormLoading(false);
          return;
        }

        setIsFormLoading(true);
        const data = await triggerGetForm(parseInt(subId)).unwrap();
        setForm({
          ...data,
          fieldValues: normalizeFormFieldValues(data.fieldValues),
        });
        currentFormId.current = data.id;

        try {
          const img = tryParse(
            tryParse(data.metadata?.cache)?.referenceImage
          )[0].value;
          setFormReferenceImage(img);
        } catch {
          /* no reference image */
        }
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }

      setIsFormLoading(false);
    };

    void getData();
  }, [subId]);

  // Apply loaded submission values once both the submission and the
  // version-pinned metadata are available
  useEffect(() => {
    if (!form || !formDefinition || formMeta.length === 0) return;

    const values: IFormData = {};
    const displayValues: IFormData = {};
    const loadedValueIds: Record<string, number> = {};
    const loadedOptionRowIds: Record<string, Record<string, number>> = {};
    const fieldTypeByName = new Map(formMeta.map(f => [f.name, f.type]));
    form.fieldValues?.forEach(fieldValue => {
      const name = resolveQualifiedFieldValueName(fieldValue, formDefinition);
      if (fieldTypeByName.get(name) === 'select-multiple') {
        if (!fieldValue.value) return;
        const selected = (values[name] as ISelectOption[] | undefined) ?? [];
        values[name] = [
          ...selected,
          {id: fieldValue.value, label: fieldValue.displayValue ?? ''},
        ];
        displayValues[name] = displayValues[name]
          ? `${displayValues[name]}, ${fieldValue.displayValue}`
          : fieldValue.displayValue;
        loadedOptionRowIds[name] = {
          ...loadedOptionRowIds[name],
          [fieldValue.value]: fieldValue.id,
        };
      } else {
        values[name] = fieldValue.value ?? '';
        displayValues[name] = fieldValue.displayValue;
        loadedValueIds[name] = fieldValue.id;
      }
    });
    values[identifierFieldName] = form.shortDescription;
    displayValues[identifierFieldName] = form.shortDescription;
    loadedValueIdsRef.current = loadedValueIds;
    loadedOptionRowIdsRef.current = loadedOptionRowIds;

    resetForm(values, displayValues);
  }, [form, formDefinition, formMeta, resetForm]);

  // Load form definition (with version resolution)
  useEffect(() => {
    const getData = async () => {
      setIsDefinitionLoading(true);
      try {
        if (defId === undefined) return;
        if (subId !== undefined && formVersion === undefined) return;

        let data: FormDefinitionResponse | undefined = undefined;
        if (subId === undefined && formVersion === undefined) {
          // The renderer requires the flattened definition: child fields
          // inlined into formFields with the host field's visibility rules.
          data = await triggerGetFormDefinition({
            id: parseInt(defId),
            flattenSubform: true,
          }).unwrap();
        } else {
          data = await triggerGetFormDefinitionVersion({
            id: parseInt(defId),
            version: formVersion!,
            flattenSubform: true,
          }).unwrap();
        }

        const normalizedData = normalizeFormDefinitionSubFormFieldIds(data);
        setFormName(normalizedData?.name ?? 'Form');
        setFormDefinition(normalizedData);
        const fields = mapFormDefinitionResponseToFormFieldsV2(
          normalizedData,
          false
        );
        setFormMeta(fields);
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }
      setIsDefinitionLoading(false);
    };

    void getData();
  }, [defId, formVersion]);

  const getMappedFieldValues = (
    requireFieldId: boolean
  ): FormFieldValueRequest[] => {
    return formApi
      .getSubmissionEntries()
      .filter(entry => !!entry.name && entry.name !== identifierFieldName)
      .flatMap(entry => {
        const {hostInvariantId, innerInvariantId} = splitQualifiedFieldName(
          entry.name
        );
        const matched = formDefinition!.formFields.find(field =>
          hostInvariantId
            ? field.invariantId === innerInvariantId &&
              field.subFormFieldInvariantId === hostInvariantId
            : field.invariantId === innerInvariantId &&
              field.subFormFieldId === undefined
        );
        const formFieldId = requireFieldId ? matched!.id! : matched?.id ?? 0;
        const id = loadedValueIdsRef.current[entry.name];

        if (entry.field.type === 'select-multiple') {
          const rowIds = loadedOptionRowIdsRef.current[entry.name] ?? {};
          const selected = Array.isArray(entry.value)
            ? (entry.value as ISelectOption[])
            : [];
          return selected.map(option => {
            const optionId = String(option.id);
            const rowId = rowIds[optionId];
            return {
              ...(rowId !== undefined && {id: rowId}),
              formFieldId,
              subFormFieldId: matched?.subFormFieldId,
              value: optionId,
              displayValue: option.label,
            };
          });
        }

        return [
          {
            ...(id !== undefined && {id}),
            formFieldId,
            subFormFieldId: matched?.subFormFieldId,
            value: entry.value,
            displayValue: entry.displayValue,
          },
        ];
      });
  };

  const getHostSubformFieldValues = (): FormFieldValueRequest[] => {
    if (!form || !formDefinition) return [];

    const hostFieldIds = new Set(
      formDefinition.formFields
        .filter(
          field =>
            field.formFieldType === FormFieldTypeEnum.Subform &&
            !field.subFormFieldInvariantId
        )
        .map(field => field.id)
    );

    return (form.fieldValues ?? [])
      .filter(
        fieldValue =>
          fieldValue.subFormFieldId === undefined &&
          hostFieldIds.has(fieldValue.formField.id)
      )
      .map(fieldValue => ({
        id: fieldValue.id,
        formFieldId: fieldValue.formField.id,
        value: fieldValue.value,
        displayValue: '',
      }));
  };

  const handleUpdateForm = async (): Promise<ITransitioningResponse> => {
    try {
      setIsSavePending(true);

      const values = formApi.methods.getValues();
      const mappedFieldValues = getMappedFieldValues(true);
      const mappedFieldValueKeys = new Set(
        mappedFieldValues.map(entry =>
          buildQualifiedFieldName(
            String(entry.subFormFieldId ?? ''),
            String(entry.formFieldId)
          )
        )
      );
      const hostFieldValues = getHostSubformFieldValues().filter(
        entry =>
          !mappedFieldValueKeys.has(
            buildQualifiedFieldName('', String(entry.formFieldId))
          )
      );

      const body = {
        id: parseInt(String(subId), 10),
        shortDescription: values[identifierFieldName],
        formDefinitionId: formDefinition!.id,
        formDefinitionVersion: formDefinition!.version,
        metadata: form?.metadata,
        targetFieldValues: [...mappedFieldValues, ...hostFieldValues],
      } as FormRequest;

      const resp = await updateForm(body).unwrap();
      setAlert(`${formDefinition?.title} was successfully updated`, 'success');
      currentFormId.current = resp.id;
      return {
        continue: true,
        overrideTransitionBaseUrl: `form/${currentFormId.current}/transition`,
      };
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
      return {continue: false};
    }
  };

  // Only plain transitions replay server-side; form/custom-handler transitions
  // drive navigation instead of a state change.
  const queuedTransitionFor = (
    transition?: IStateMachineTransition
  ): {name: string} | undefined =>
    transition &&
    (transition.action === 'transition' || transition.action === undefined)
      ? {name: transition.name}
      : undefined;

  const handleCreateForm = async (
    transition?: IStateMachineTransition
  ): Promise<FormSaveResult> => {
    setIsSavePending(true);
    try {
      const mappedFieldValues = getMappedFieldValues(false);

      // Add subform fields for back-end reference
      formDefinition?.formFields
        .filter(
          item =>
            item.formFieldType === FormFieldTypeEnum.Subform &&
            !item.subFormFieldInvariantId
        )
        .forEach(subformField => {
          mappedFieldValues.push({
            formFieldId: subformField.id,
            value: '',
            displayValue: '',
          });
        });

      const extTabId = extensionTabId ? Number(extensionTabId) : undefined;
      const extEntityType = targetEntityType
        ? (Number(targetEntityType) as EntityIdentifierEnum)
        : undefined;
      const extEntityId = targetEntityId ? Number(targetEntityId) : undefined;

      const values = formApi.methods.getValues();
      const queuedTransition = queuedTransitionFor(transition);
      const body = {
        shortDescription: values[identifierFieldName],
        formDefinitionId: formDefinition?.id,
        formDefinitionVersion: formDefinition?.version,
        metadata: {...formDefinition?.metadata, taskAssignmentId},
        targetFieldValues: mappedFieldValues,
        ...(extTabId &&
          extEntityType &&
          extEntityId && {
            extensionTabId: extTabId,
            targetEntityType: extEntityType,
            targetEntityId: extEntityId,
          }),
        ...(queuedTransition && {queuedTransition}),
      } as OfflineFormCreateRequest;

      const resp = await createForm(body).unwrap();

      if (isOfflineQueuedResult(resp)) {
        // The enqueue queued the captured uploads with it, so cleanup must not run.
        uploadsQueuedRef.current = true;
        const title = formDefinition?.title ?? 'Your form';
        setAlert(
          isEffectivelyOffline()
            ? `${title} was saved and will be submitted automatically when you are back online`
            : `${title} was saved and is being submitted`,
          'success'
        );
        setIsSavePending(false);
        return {continue: false, offlineQueued: true};
      }

      setAlert(`${formDefinition?.title} was successfully created`, 'success');
      currentFormId.current = resp.id;
      return {
        continue: true,
        overrideTransitionBaseUrl: `form/${currentFormId.current}/transition`,
      };
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
      return {continue: false};
    }
  };

  const handleValidateAndSave = async (
    transition?: IStateMachineTransition
  ): Promise<FormSaveResult> => {
    const isValid = await formApi.validateForm();
    if (!isValid) {
      return {continue: false};
    }
    if (subId === undefined) {
      return await handleCreateForm(transition);
    }
    return await handleUpdateForm();
  };

  // State machine with "Save & ..." prefixed transition titles
  const [machineBase] = useStateMachine({
    url: `${ApiEndpoints.getStaticForm}/state-machine`,
  });
  const stateMachine = machineBase
    ? ({
        states: machineBase.states,
        transitions: machineBase.transitions.map(t => ({
          ...t,
          title: `Save & ${t.title}`,
        })),
      } as IStateMachine)
    : undefined;

  return {
    form,
    formDefinition,
    formName,
    formApi,
    formMeta,
    formReferenceImage,
    isLoading,
    isSavePending,
    currentFormId,
    stateMachine,
    handleValidateAndSave,
  };
};
